/**
 * Phase 1c — entity-linked liabilities.
 * Ported from frontend/src/utils/structureLiabilitiesUtils.js — omits `getStructureLiabilityWarnings`
 * and `buildOwnershipOptions` (UI form/warning-banner helpers, never called by calculations/).
 */
import {
    entityOwnershipValue as _entityOwnershipValue,
    getEntityIdFromOwnership,
    isEntityOwnership,
} from '../constants/clientStructureTypes.ts';
import { ASSET_TYPES } from '../constants/assetTypes.ts';
import { calculateLoanRepayment } from '../taxAndLoanUtils.ts';
import type { FactFind } from '../factFindTypes.ts';
import { isCapitalisingLoan } from './capitalisingLoans.ts';

const LIQUID_ASSET_TYPES = new Set(ASSET_TYPES.filter((t) => t.isLiquid).map((t) => t.id));

export interface LiabilityItem {
    type?: string;
    excludeFromCashflow?: boolean;
    balance?: number;
    interestRate?: number;
    loanTerm?: number;
    frequency?: string;
    loanType?: string;
    repaymentCalculation?: string;
    repayments?: number;
    ownership?: string;
    assetLink?: string;
}

/**
 * The debt types whose repayment can be DERIVED, because they run to a term and amortise. A credit card
 * revolves and has no term, so there is nothing to derive from and the client's own payment is the only
 * honest figure.
 */
const TERM_LOAN_TYPES: ReadonlySet<string> = new Set(['mortgages', 'personalLoans']);

/** True where a debt runs to a term, and so can be asked for a loan term and a P&I / interest-only basis. */
export function isTermLoanType(liabilityType: string | undefined): boolean {
    return TERM_LOAN_TYPES.has(String(liabilityType ?? ''));
}

/**
 * Whether this liability's repayment is DERIVED from its balance, rate and term rather than typed by
 * the client — and therefore whether the fact-find's repayment field should be read-only.
 *
 * Exported because the form must ask the same question this module answers. It used to decide for
 * itself, on `repaymentCalculation === 'auto'` alone, and the two drifted: every new liability is
 * created 'auto', but the form offered the switch out of 'auto' — and the rate and term it needs —
 * for mortgages only. A personal loan was therefore pinned to a derivation with nothing to derive
 * from, displaying $0 while silently discarding whatever was typed over it. One exported rule removes
 * the possibility rather than fixing the instance.
 *
 * Deliberately says nothing about the liability's TYPE. Plenty of liabilities reach the engine with no
 * `type` at all — the Support at Home suggestion engine passes a `loans` category whose rows carry only
 * a description — and requiring a known type here would stop those being repaid at all. Which debts get
 * ASKED for a term is a question for the form (see `isTermLoanType`); whether a repayment CAN be
 * derived is this one, and it depends only on having the three numbers.
 */
export function derivesRepaymentAutomatically(item: LiabilityItem): boolean {
    return item.repaymentCalculation === 'auto'
        && (item.balance || 0) > 0
        && (item.interestRate || 0) > 0
        && (item.loanTerm || 0) > 0;
}

export function calculateAnnualLoanRepayment(item: LiabilityItem): number {
    if (isCapitalisingLoan(item) || item.excludeFromCashflow === true) return 0;

    const balance = item.balance || 0;
    const rate = item.interestRate || 0;
    const term = item.loanTerm || 0;
    const freq = item.frequency || 'monthly';
    const loanType = item.loanType || 'pi';

    const periodicRepayment = derivesRepaymentAutomatically(item)
        ? calculateLoanRepayment(balance, rate, term, freq, loanType)
        : (item.repayments || 0);

    switch (freq) {
        case 'weekly':
            return periodicRepayment * 52;
        case 'fortnightly':
            return periodicRepayment * 26;
        default:
            return periodicRepayment * 12;
    }
}

/** Entity id if this liability belongs to a trust/company pool (explicit ownership or secured asset link). */
export function resolveLiabilityEntityId(liability: LiabilityItem, factFind: FactFind): string | null {
    if (isEntityOwnership(liability.ownership)) {
        const id = getEntityIdFromOwnership(liability.ownership);
        return id && id !== '__unsure__' ? id : null;
    }

    if (liability.type !== 'mortgages') return null;

    if (liability.assetLink === 'home' || liability.assetLink === 'none') {
        if (factFind.home?.ownership === 'other_entity' && factFind.home?.entityId) {
            return factFind.home.entityId;
        }
        return null;
    }

    const prop = (factFind.assets?.investmentProperty || []).find(
        (p) => String(p.id) === String(liability.assetLink),
    );
    if (prop && isEntityOwnership(prop.ownership)) {
        return getEntityIdFromOwnership(prop.ownership);
    }
    return null;
}

export function isEntityLiability(liability: LiabilityItem, factFind: FactFind): boolean {
    return resolveLiabilityEntityId(liability, factFind) != null;
}

export function isLiquidAssetType(typeId: string): boolean {
    return LIQUID_ASSET_TYPES.has(typeId);
}
